// Her words as subtitles on the right of the room (D_Desk "lines" + "choices" + the input line). No panel: a soft
// scrim behind, her name above her line (accent), your lines right-aligned in italics ("YOU · SAID" when spoken),
// one-line notes for what changed, choices under her line ("— Looks good"). Older lines fade upward; with the mouse
// still only the last two stay. The history scrolls (all lines readable while scrolled up). The lines come from the
// phone chat's own derive() (state.messages + the live stream), so every message kind has a rendering here.
import type { Ref } from 'preact';
import { useRef, useState } from 'preact/hooks';
import type { AppState, ChangeData, DoneNoteData, Message, PlanData, ProposedCard, RouteStop, StartOfferData, StuckChatData, SummaryData, WorkCheckData } from '@shared/types';
import { C } from '@shared/content/characters';
import { CRISIS } from '@shared/content/lines';
import { doneNoteText } from '@shared/content/stuck';
import { startedNote, workEndedNote } from '@shared/content/work';
import { taskShort } from '@shared/rules';
import { DeskTalkBar, Icon, fmtMs, type IconName } from '../../components';
import type { Clip, RecorderError } from '../../audio/recorder';
import type { Item } from '../day/ChatScreen';
import { dayOf } from '../day/util';
import { NOTE, TALK } from './roomCopy';
import s from './room.module.css';

export type Line =
  | { k: 'time'; key: string; text: string }
  | { k: 'her'; key: string; text: string; dots?: boolean }
  | { k: 'you'; key: string; text: string; who: string; image?: string | null }
  | { k: 'note'; key: string; text: string; icon: IconName; link?: { label: string; run: () => void } | null; dim?: boolean; flame?: boolean }
  | { k: 'voice'; key: string; ms: number; levels: number[]; failed: boolean }
  | { k: 'crisis'; key: string; text: string };

export interface Choice { label: string; run: () => void; strong?: boolean; muted?: boolean; busy?: boolean }

/** What the notes' links do (the room owns navigation and the actions). */
export interface LineActions {
  undoChange: (changeId: string) => void;
  openWeek: () => void;
  openUnlock: (stop: RouteStop | null) => void;
  openPlanTime: () => void;
  openCrisis: () => void;
  planTime: string;
  streakFoot: (data: SummaryData, onToday: boolean) => string | null;
}

const fillT = (t: string, v: Record<string, string | number>) => t.replace(/\{(\w+)\}/g, (m, k) => (v[k] != null ? String(v[k]) : m));
const words = (n: number) => ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'][n] || String(n);
/** "Today, 9:00 AM" -> "TODAY · 9:00 AM" (D_Desk time markers). */
const timeMark = (t: string) => { const i = t.lastIndexOf(', '); return (i > 0 ? t.slice(0, i) + ' · ' + t.slice(i + 2) : t).toUpperCase(); };

/** derive() items -> subtitle lines. */
export function toLines(items: Item[], st: AppState, a: LineActions): Line[] {
  const out: Line[] = [];
  const tz = st.user.tz;
  const her = (key: string, text: string | null | undefined) => { if (text && text.trim()) out.push({ k: 'her', key, text }); };
  const note = (key: string, text: string, icon: IconName, extra: Partial<Extract<Line, { k: 'note' }>> = {}) => out.push({ k: 'note', key, text, icon, ...extra });
  const cardById = (id: string) => st.plan.cards.find((x) => x.id === id) || st.tomorrow.find((x) => x.id === id) || null;
  const summary = (key: string, line: string | null, d: SummaryData, onToday: boolean) => {
    her(key + 'l', line);
    note(key + 'n', fillT(NOTE.summary, { d: words(d.done) }) + (d.moved ? fillT(NOTE.summaryMoved, { k: words(d.moved).toLowerCase() }) : ''), 'check');
    const foot = a.streakFoot(d, onToday);
    if (foot) note(key + 'f', foot, 'flame', { flame: true });
    if (onToday) note(key + 'p', fillT(NOTE.planAt, { x: a.planTime }), 'clock', { link: { label: NOTE.changeTime, run: a.openPlanTime } });
  };
  // A stuck ask closed without a reason (Never mind, or Stuck on another card) leaves no trace, nor does the user's
  // line before it (D_Desk keeps only the stuck talks that led somewhere).
  const gone = new Set<string>();
  for (const it of items) {
    if (it.k !== 'msg' || it.m.kind !== 'stuck') continue;
    const d = it.m.data as StuckChatData | undefined;
    if (d && d.status === 'closed' && d.step === 'ask' && !d.reason) { gone.add(it.m.id); if (d.said) gone.add(d.said); }
  }
  for (const it of items) {
    if (it.k === 'msg' && gone.has(it.m.id)) continue;
    switch (it.k) {
      case 'div': out.push({ k: 'time', key: it.key, text: timeMark(it.text) }); break;
      case 'stream': case 'heard': her(it.key, it.text); break;
      case 'typing': out.push({ k: 'her', key: it.key, text: '', dots: true }); break;
      case 'echo':
        if (it.out.kind === 'text') out.push({ k: 'you', key: it.key, text: it.out.text, who: TALK.you });
        else out.push({ k: 'you', key: it.key, text: '', who: TALK.photo, image: it.out.thumb });
        break;
      case 'voice': out.push({ k: 'voice', key: it.key, ms: it.v.clip.ms, levels: it.v.clip.levels || [], failed: it.v.failed }); break;
      case 'first': note(it.key, fillT(NOTE.first, { t: it.title }), 'check'); break;
      case 'note': note(it.key, `${it.head}. ${it.text}`, 'flame', { flame: true }); break;
      case 'eve': summary(it.key, it.line, it.data, true); break;
      case 'msg': msgLines(it.key, it.m); break;
    }
  }
  // No time marker without a line under it.
  return out.filter((x, i) => x.k !== 'time' || (i < out.length - 1 && out[i + 1].k !== 'time'));

  function msgLines(key: string, m: Message) {
    if (m.role === 'user') {
      if (m.image) out.push({ k: 'you', key, text: m.text || '', who: TALK.photo, image: /^(data:|\/|https?:)/.test(m.image) ? m.image : null });
      else out.push({ k: 'you', key, text: m.text, who: m.voice ? TALK.said : TALK.you });
      return;
    }
    switch (m.kind) {
      case 'plan': {
        const d = m.data as PlanData | undefined;
        // On the desk while it waits ("Looks good" is a choice); once agreed, D_Desk's "YOU: Looks good."
        if (d?.status === 'accepted') out.push({ k: 'you', key, text: NOTE.looksGood, who: TALK.you });
        return;
      }
      case 'change': {
        const d = m.data as ChangeData | undefined;
        if (!d) return;
        const onlySplit = d.items.every((x) => x.type === 'split');
        d.items.forEach((x, i) => {
          const card = x.cardIds.map(cardById).find(Boolean) || null;
          const t = x.type === 'moved' ? fillT(NOTE.moved, { t: taskShort(x.title) })
            : x.type === 'retimed' ? fillT(NOTE.retimed, { t: taskShort(x.title), x: x.detail || card?.time || '' })
            : x.type === 'split' ? fillT(NOTE.split, { t: taskShort(x.title), k: x.cardIds.length })
            : x.type === 'removed' ? fillT(x.detail === 'Not in the plan' ? NOTE.notInPlan : NOTE.removed, { t: x.title })
            : fillT(NOTE.added, { t: x.title, x: card?.time || x.detail || '' });
          const icon: IconName = x.type === 'moved' || x.type === 'retimed' ? 'arrowRight' : x.type === 'removed' ? 'close' : 'plus';
          const last = i === d.items.length - 1;
          const link = last && !d.undone ? { label: onlySplit ? NOTE.keepOne : NOTE.undo, run: () => a.undoChange(d.changeId) } : null;
          note(key + ':' + i, t + (last && d.undone ? ` · ${NOTE.undone}` : ''), icon, { link, dim: !!d.undone });
        });
        return;
      }
      case 'summary': {
        const d = m.data as SummaryData | undefined;
        if (!d) { her(key, m.text); return; }
        summary(key, m.text, d, dayOf(m.ts, tz) === st.today);
        return;
      }
      case 'tomorrow': {
        const d = m.data as { card?: ProposedCard; day?: string } | undefined;
        if (!d?.card) return;
        const tonight = d.day === st.today && st.dayIndex === 0;
        note(key, fillT(tonight ? NOTE.tonight : NOTE.tomorrow, { x: d.card.time, t: d.card.title }), 'calendar');
        return;
      }
      case 'nudge': {
        const d = m.data as { card?: ProposedCard; status?: string } | undefined;
        her(key + 'l', m.text);
        if (d?.card) {
          const tail = d.status === 'accepted' ? ` · ${NOTE.inToday}` : d.status && d.status !== 'pending' ? ` · ${NOTE.expired}` : '';
          note(key + 'n', fillT(NOTE.nudge, { t: d.card.title, k: d.card.min }) + tail, d.status === 'accepted' ? 'check' : 'calendar');
        }
        return;
      }
      case 'crisis': out.push({ k: 'crisis', key, text: CRISIS.title }); return;
      case 'week': {
        her(key + 'l', m.text);
        const w = st.week;
        const x = w ? `${w.cards} ${w.cards === 1 ? 'card' : 'cards'} · ${w.lit} ${w.lit === 1 ? 'day' : 'days'} lit` : '';
        note(key + 'n', fillT(NOTE.week, { x }), 'gift', { link: { label: NOTE.seeWeek, run: a.openWeek } });
        return;
      }
      case 'unlock': {
        const stop = (m.data || null) as RouteStop | null;
        note(key, m.text || stop?.label || '', 'star', { link: { label: stop?.type === 'chap' ? NOTE.read : NOTE.see, run: () => a.openUnlock(stop) } });
        return;
      }
      case 'stuck': {
        const d = m.data as StuckChatData | undefined;
        her(key + 'l', m.text);
        if (d?.out) note(key + 'o', d.out, d.act === 'later' ? 'arrowRight' : d.act === 'split' ? 'plus' : 'play');
        return;
      }
      case 'done': {
        const d = m.data as DoneNoteData | undefined;
        if (!d || d.undone) return;
        note(key + 'n', m.text || doneNoteText(m.ch, d), 'check');
        her(key + 'l', d.line);
        return;
      }
      case 'work': {
        // Her "Done, or keep going?" (11-05): the answers are choices under it while it waits (Room.tsx).
        const d = m.data as WorkCheckData | undefined;
        her(key + 'l', m.text);
        if (d?.status === 'ended') note(key + 'e', workEndedNote(d.title), 'arrowRight');
        return;
      }
      case 'start': {
        // Her Start together (11-05): a choice under her line while it waits; once taken, a note.
        const d = m.data as StartOfferData | undefined;
        if (d?.status === 'started') note(key, startedNote(m.ch, d.title), 'play');
        return;
      }
      case 'limit': case 'offer': case 'planned': case 'text': default:
        her(key, m.text);
    }
  }
}

interface RoomTalkProps {
  st: AppState;
  lines: Line[];
  choices: Choice[];
  /** Mouse still: only the last two lines stay. */
  quiet: boolean;
  draft: string;
  setDraft: (v: string) => void;
  onSend: () => void;
  onAudio: (clip: Clip) => void;
  onMicError: (e: RecorderError) => void;
  onToast: (t: string) => void;
  onRetryVoice: () => void;
  onCrisis: () => void;
  sending: boolean;
  inputRef: Ref<HTMLInputElement>;
}

export function RoomTalk({ st, lines, choices, quiet, draft, setDraft, onSend, onAudio, onMicError, onToast, onRetryVoice, onCrisis, sending, inputRef }: RoomTalkProps) {
  const c = C[st.ch];
  const log = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const n = lines.length;
  const op = (i: number) => {
    const d = n - 1 - i;
    if (scrolled) return 1;
    if (quiet) return d < 2 ? 1 : 0;
    return d < 4 ? 1 : Math.max(0.32, 1 - (d - 3) * 0.14);
  };
  return (
    <div class={s.col} aria-label={fillT(TALK.talking, { n: c.name })} role="region">
      <div
        ref={log} class={`${s.log} xm-scroll`} role="log" aria-live="polite" aria-label={`Chat with ${c.name}`}
        onScroll={() => { const el = log.current; if (el) { const up = Math.abs(el.scrollTop) > 8; if (up !== scrolled) setScrolled(up); } }}
      >
        <div class={s.lines}>
          {lines.map((ln, i) => <LineView key={ln.key} ln={ln} tag={ln.k === 'her' && !(i > 0 && lines[i - 1].k === 'her')} name={c.name} op={op(i)} onRetryVoice={onRetryVoice} onCrisis={onCrisis} />)}
        </div>
      </div>
      {choices.length > 0 && (
        <div class={s.choices} role="group" aria-label={fillT(TALK.answer, { n: c.name })}>
          {choices.map((x) => (
            <button key={x.label} type="button" class={s.choice} data-strong={x.strong ? 'true' : 'false'} data-muted={x.muted ? 'true' : 'false'} aria-busy={x.busy ? 'true' : undefined} onClick={x.run}>
              <span class={s.dash} aria-hidden="true" /><span>{x.label}</span>
            </button>
          ))}
        </div>
      )}
      <DeskTalkBar
        look="line" name={c.name} placeholder={fillT(TALK.placeholder, { n: c.name })}
        value={draft} onInput={setDraft} inputRef={inputRef} onSend={onSend} onAudio={onAudio}
        onError={onMicError} onTooShort={() => onToast('Too short. Nothing was sent.')} onCancelled={() => onToast('Cancelled. Nothing was sent.')}
        disabled={sending}
      />
    </div>
  );
}

function LineView({ ln, tag, name, op, onRetryVoice, onCrisis }: { ln: Line; tag: boolean; name: string; op: number; onRetryVoice: () => void; onCrisis: () => void }) {
  const style = { opacity: op };
  if (ln.k === 'time') return <p class={s.time} style={style}>{ln.text}</p>;
  if (ln.k === 'her') {
    return (
      <div class={s.her} style={style}>
        {tag && <span class={s.herName}>{name.toUpperCase()}</span>}
        {ln.dots
          ? <p class={s.herText} aria-label={`${name} is typing`}><span class={s.dots} aria-hidden="true"><span /><span /><span /></span></p>
          : <p class={s.herText}><span class="xm-sr-only">{`${name}: `}</span>{ln.text}</p>}
      </div>
    );
  }
  if (ln.k === 'you') {
    return (
      <div class={s.you} style={style}>
        <span class={s.youWho}>{ln.who}</span>
        {ln.image && <img class={s.youImg} src={ln.image} alt="Your photo" />}
        {ln.text && <p class={s.youText}>{ln.text}</p>}
      </div>
    );
  }
  if (ln.k === 'voice') {
    const bars = (ln.levels.length ? ln.levels : Array.from({ length: 20 }, () => 0.3)).slice(-20);
    return (
      <div class={s.you} style={style}>
        <span class={s.youWho}>{TALK.said}</span>
        <span class={s.voice} role="img" aria-label={`Your voice message, ${fmtMs(ln.ms)}`}>
          {ln.failed && <span class={s.voiceBang} aria-hidden="true">!</span>}
          <Icon name="mic" size={15} />
          <span class={s.voiceBars} aria-hidden="true">{bars.map((v, i) => <span key={i} style={{ height: Math.max(3, Math.round(18 * Math.min(1, 0.15 + v))) }} />)}</span>
          <span class={s.voiceLen}>{fmtMs(ln.ms)}</span>
        </span>
        {ln.failed
          ? <p class={s.voiceSub}>{NOTE.notThrough} <button type="button" class={s.link} onClick={onRetryVoice}>{NOTE.tryAgain}</button></p>
          : <p class={s.voiceSub}>{NOTE.working}</p>}
      </div>
    );
  }
  if (ln.k === 'crisis') {
    return (
      <button type="button" class={s.crisis} style={style} onClick={onCrisis}>
        <Icon name="shield" size={18} color="var(--acc)" /><span>{ln.text}</span><Icon name="chevron" size={16} />
      </button>
    );
  }
  return (
    <p class={s.note} data-dim={ln.dim ? 'true' : 'false'} style={style}>
      <Icon name={ln.icon} size={14} strokeWidth={ln.flame ? 1.8 : 2.6} color="var(--acc)" fill={ln.flame ? 'var(--acc)' : undefined} />
      <span>{ln.text}{ln.link && <> <button type="button" class={s.link} onClick={ln.link.run}>{ln.link.label}</button></>}</span>
    </p>
  );
}

